import React, { useState, useEffect } from 'react';
import { Modal, Button, Form, Alert } from 'react-bootstrap';
import { toast } from 'react-toastify';
import { useSelector } from 'react-redux';
import { useDropboxFiles } from '../../hooks/useDropboxFiles';
import { useDropboxFolders } from '../../hooks/useDropboxFolders';
import { filenameIncludesExtension } from '../../utils/nameUtils';

interface RenameModalProps {
  item: any;
  type: 'file' | 'folder';
  onClose: () => void;
  onSuccess: () => void;
}

const RenameModal: React.FC<RenameModalProps> = ({ item, type, onClose, onSuccess }) => {
  const [newName, setNewName] = useState('');
  const [nameError, setNameError] = useState<string | null>(null);
  const [suggestedName, setSuggestedName] = useState<string | null>(null);
  const { renameFile, loading: fileLoading } = useDropboxFiles();
  const { renameFolder, loading: folderLoading } = useDropboxFolders();
  const { folders, files } = useSelector((state: any) => state.dropbox);
  
  const loading = fileLoading || folderLoading;

  useEffect(() => {
    if (item) {
      // For files, remove extension from initial name
      if (type === 'file' && item.extension) {
        const nameWithoutExt = item.name.replace(new RegExp(`\\.${item.extension}$`), '');
        setNewName(nameWithoutExt);
      } else {
        setNewName(item.name);
      }
    }
  }, [item, type]);

  // Generate a unique name suggestion
  const generateUniqueName = (baseName: string, extension?: string): string => {
    const currentFolderId = type === 'file' ? item.folder_id : item.parent_id;
    
    // Get existing names in the same folder
    const existingNames = type === 'file'
      ? files
          .filter((f: any) => f.folder_id === currentFolderId && f.id !== item.id)
          .map((f: any) => f.name.toLowerCase())
      : folders
          .filter((f: any) => f.parent_id === currentFolderId && f.id !== item.id)
          .map((f: any) => f.name.toLowerCase());

    // Try "name (Copy)" first
    let suggestion = `${baseName} (Copy)`;
    let fullName = extension ? `${suggestion}.${extension}` : suggestion;
    
    if (!existingNames.includes(fullName.toLowerCase())) {
      return suggestion;
    }

    // Try "name (2)", "name (3)", etc.
    let counter = 2;
    while (counter < 100) {
      suggestion = `${baseName} (${counter})`;
      fullName = extension ? `${suggestion}.${extension}` : suggestion;
      
      if (!existingNames.includes(fullName.toLowerCase())) {
        return suggestion;
      }
      counter++;
    }

    // Fallback with timestamp
    const timestamp = Date.now();
    return `${baseName} (${timestamp})`;
  };

  // Check for duplicate names
  const checkDuplicateName = (name: string): boolean => {
    const currentFolderId = type === 'file' ? item.folder_id : item.parent_id;
    
    // Add extension back for files
    let finalName = name.trim();
    if (type === 'file' && item.extension) {
      finalName = `${finalName}.${item.extension}`;
    }

    // Check if name actually changed
    if (finalName.toLowerCase() === item.name.toLowerCase()) {
      setNameError(null);
      setSuggestedName(null);
      return false;
    }

    // Check for duplicates
    const isDuplicate = type === 'file'
      ? files.some((f: any) => 
          f.folder_id === currentFolderId && 
          f.id !== item.id && 
          f.name.toLowerCase() === finalName.toLowerCase()
        )
      : folders.some((f: any) => 
          f.parent_id === currentFolderId && 
          f.id !== item.id && 
          f.name.toLowerCase() === finalName.toLowerCase()
        );

    if (isDuplicate) {
      setNameError(`A ${type} with this name already exists in this location`);
      const suggestion = generateUniqueName(name.trim(), type === 'file' ? item.extension : undefined);
      setSuggestedName(suggestion);
      return true;
    }

    setNameError(null);
    setSuggestedName(null);
    return false;
  };

  // Handle name change with validation
  const handleNameChange = (value: string) => {
    setNewName(value);
    if (value.trim()) {
      checkDuplicateName(value);
    } else {
      setNameError(null);
      setSuggestedName(null);
    }
  };

  // Apply suggested name
  const applySuggestion = () => {
    if (suggestedName) {
      setNewName(suggestedName);
      setNameError(null);
      setSuggestedName(null);
    }
  };

  // Apply suggestion and submit immediately
  const applySuggestionAndRename = async () => {
    if (!suggestedName) return;
    
    // Add extension back for files
    let finalName = suggestedName.trim();
    if (type === 'file' && item.extension) {
      finalName = `${finalName}.${item.extension}`;
    }

    let result;
    if (type === 'file') {
      result = await renameFile(item.id, finalName);
    } else {
      result = await renameFolder(item.id, finalName);
    }
    
    if (result) {
      onSuccess();
      onClose();
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!newName.trim()) {
      toast.error('Name cannot be empty');
      return;
    }

    // Check for duplicates before submitting
    if (checkDuplicateName(newName)) {
      return;
    }

    // Add extension back for files
    let finalName = newName.trim();
    if (type === 'file' && item.extension) {
      finalName = `${finalName}.${item.extension}`;
    }

    // Check if name actually changed
    if (finalName === item.name) {
      toast.info('Name unchanged');
      onClose();
      return;
    }

    let result;
    if (type === 'file') {
      result = await renameFile(item.id, finalName);
    } else {
      result = await renameFolder(item.id, finalName);
    }
    
    if (result) {
      onSuccess();
      onClose();
    }
  };

  return (
    <Modal show={true} onHide={onClose} centered>
      <Modal.Header closeButton>
        <Modal.Title>Rename {type === 'file' ? 'File' : 'Folder'}</Modal.Title>
      </Modal.Header>
      <Form onSubmit={handleSubmit}>
        <Modal.Body>
          <Form.Group>
            <Form.Label>New Name</Form.Label>
            <Form.Control
              type="text"
              value={newName}
              onChange={(e) => handleNameChange(e.target.value)}
              placeholder={`Enter ${type} name`}
              autoFocus
              required
              isInvalid={!!nameError}
            />
            {type === 'file' && item.extension && !nameError && (
              <Form.Text className="text-muted">
                Extension .{item.extension} will be added automatically
              </Form.Text>
            )}
            {nameError && (
              <Form.Control.Feedback type="invalid">
                {nameError}
              </Form.Control.Feedback>
            )}
          </Form.Group>

          {suggestedName && (
            <Alert variant="info" className="mt-3 mb-0">
              <div className="d-flex align-items-center justify-content-between">
                <div>
                  <strong>Suggestion:</strong> {suggestedName}
                  {type === 'file' && item.extension && !filenameIncludesExtension(suggestedName, item.extension) && `.${item.extension}`}
                </div>
                <div className="d-flex gap-2">
                  <Button 
                    variant="outline-info" 
                    size="sm"
                    onClick={applySuggestion}
                    disabled={loading}
                  >
                    Use This
                  </Button>
                  <Button 
                    variant="info" 
                    size="sm"
                    onClick={applySuggestionAndRename}
                    disabled={loading}
                  >
                    {loading ? 'Renaming...' : 'Apply & Rename'}
                  </Button>
                </div>
              </div>
            </Alert>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={onClose} disabled={loading}>
            Cancel
          </Button>
          <Button 
            variant="primary" 
            type="submit" 
            disabled={loading || !newName.trim() || !!nameError}
          >
            {loading ? 'Renaming...' : 'Rename'}
          </Button>
        </Modal.Footer>
      </Form>
    </Modal>
  );
};

export default RenameModal;
